import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { Box, IconButton, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ReactNode } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import { useApplyColumnFilter } from './useApplyColumnFilter';

const filterTooltipMessage = defineMessage({
  id: 'common.actions.filterByValue',
  defaultMessage: 'Filtrar por este valor',
});

export interface ApplyColumnFilterProps<TValue> {
  field: string;
  value: TValue | null | undefined;
  formatValue?: (value: TValue | null | undefined) => unknown;
  children: ReactNode;
}

/**
 * Displays cell content with a filter action that appears when the content is hovered.
 */
export function ApplyColumnFilter<TValue>({
  children,
  field,
  value,
  formatValue,
}: ApplyColumnFilterProps<TValue>) {
  const { $t } = useIntl();
  const applyFilter = useApplyColumnFilter({
    field,
    value,
    formatValue,
  });

  const canApply = value != null && value !== '';

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        '&:hover .apply-column-filter-button': {
          visibility: 'visible',
        },
      }}
    >
      <Box sx={{ minWidth: 0, width: '100%' }}>{children}</Box>
      {canApply && (
        <Tooltip title={$t(filterTooltipMessage)} placement="top">
          <IconButton
            className="apply-column-filter-button"
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              applyFilter();
            }}
            sx={{
              position: 'absolute',
              top: '50%',
              right: 0,
              transform: 'translateY(-50%)',
              visibility: 'hidden',
              backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.5),
              '&:hover': {
                backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.5),
              },
            }}
          >
            <FilterAltOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
